import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Trophy, Crown, Medal, TrendingUp, TrendingDown, Minus } from 'lucide-react';

// Helper component for rank/points change display
const ChangeIndicator = ({ value, isRank = false }) => {
    if (value === null || value === undefined) {
        return <span className="text-muted-foreground">-</span>;
    }
    
    if (value === 0) {
        return (
            <span className="flex items-center justify-center text-muted-foreground">
                <Minus className="w-3 h-3 mr-1" />
                0
            </span>
        );
    }
    
    // For rank: positive = improved (show green with up arrow)
    // For points: positive = gained points (show green with up arrow)
    const isPositive = value > 0;
    
    return (
        <span className={`flex items-center justify-center font-medium ${isPositive ? 'text-green-600' : 'text-red-500'}`}>
            {isPositive ? (
                <TrendingUp className="w-3 h-3 mr-1" />
            ) : (
                <TrendingDown className="w-3 h-3 mr-1" />
            )}
            {isPositive ? '+' : ''}{value}
        </span>
    );
};

export default function LeaderboardPage() {
    const { api, user } = useAuth();
    const [leaderboard, setLeaderboard] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchLeaderboard = async () => {
            try {
                const response = await api().get('/leaderboard');
                setLeaderboard(response.data);
            } catch (error) {
                console.error('Failed to fetch leaderboard:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchLeaderboard();
    }, [api]);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="spinner" />
            </div>
        );
    }

    const top3 = leaderboard.slice(0, 3);

    return (
        <div className="space-y-8 animate-fade-in" data-testid="leaderboard-page">
            <div>
                <h1 className="text-3xl font-heading">Rangliste</h1>
                <p className="text-muted-foreground">Wer wird Tippkönig?</p>
            </div>

            {/* Podium for Top 3 */}
            {top3.length > 0 && (
                <div className="grid grid-cols-3 gap-4 items-end">
                    {/* Second Place */}
                    <div className="order-1">
                        {top3[1] && (
                            <Card className="card-hover" data-testid="podium-2">
                                <CardContent className="p-4 text-center">
                                    <div className="w-12 h-12 mx-auto mb-2 rounded-full podium-second flex items-center justify-center">
                                        <Medal className="w-6 h-6 text-slate-700" />
                                    </div>
                                    <p className="text-lg font-heading">{top3[1].pseudonym}</p>
                                    <p className="text-3xl font-heading tabular-nums text-primary mt-2">{top3[1].total_points}</p>
                                    <p className="text-sm text-muted-foreground">{top3[1].predictions_count} Tipps</p>
                                </CardContent>
                            </Card>
                        )}
                    </div>

                    {/* First Place */}
                    <div className="order-2">
                        {top3[0] && (
                            <Card className="card-hover border-amber-300" data-testid="podium-1">
                                <CardContent className="p-6 text-center">
                                    <div className="w-16 h-16 mx-auto mb-3 rounded-full podium-first flex items-center justify-center crown-icon">
                                        <Crown className="w-8 h-8 text-amber-900" />
                                    </div>
                                    <p className="text-xl font-heading">{top3[0].pseudonym}</p>
                                    <p className="text-4xl font-heading tabular-nums text-amber-500 mt-2">{top3[0].total_points}</p>
                                    <p className="text-sm text-muted-foreground">{top3[0].predictions_count} Tipps</p>
                                </CardContent>
                            </Card>
                        )}
                    </div>

                    {/* Third Place */}
                    <div className="order-3">
                        {top3[2] && (
                            <Card className="card-hover" data-testid="podium-3">
                                <CardContent className="p-4 text-center">
                                    <div className="w-12 h-12 mx-auto mb-2 rounded-full podium-third flex items-center justify-center">
                                        <Trophy className="w-6 h-6 text-orange-800" />
                                    </div>
                                    <p className="text-lg font-heading">{top3[2].pseudonym}</p>
                                    <p className="text-3xl font-heading tabular-nums text-primary mt-2">{top3[2].total_points}</p>
                                    <p className="text-sm text-muted-foreground">{top3[2].predictions_count} Tipps</p>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                </div>
            )}

            {/* Points Info */}
            <Card className="bg-muted/30">
                <CardContent className="p-4">
                    <h3 className="font-heading text-lg mb-2">Punkteverteilung</h3>
                    <div className="grid grid-cols-3 gap-4 text-center text-sm">
                        <div>
                            <span className="text-2xl font-heading text-amber-500">6</span>
                            <p className="text-muted-foreground">Exaktes Ergebnis</p>
                        </div>
                        <div>
                            <span className="text-2xl font-heading text-primary">2</span>
                            <p className="text-muted-foreground">Korrekter Sieger</p>
                        </div>
                        <div>
                            <span className="text-2xl font-heading text-primary">2</span>
                            <p className="text-muted-foreground">Korrekte Differenz</p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Full Leaderboard Table */}
            <Card data-testid="leaderboard-table">
                <CardHeader>
                    <CardTitle>Komplette Rangliste</CardTitle>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-16">Rang</TableHead>
                                <TableHead className="w-20 text-center">+/-</TableHead>
                                <TableHead>Teilnehmer</TableHead>
                                <TableHead className="text-center">Punkte</TableHead>
                                <TableHead className="w-20 text-center">+/-</TableHead>
                                <TableHead className="text-center">Letzter Tipp</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {leaderboard.map((entry) => (
                                <TableRow 
                                    key={entry.user_id}
                                    className={entry.user_id === user?.id ? 'bg-primary/10' : ''}
                                    data-testid={`leaderboard-row-${entry.rank}`}
                                >
                                    <TableCell className="font-heading text-lg">{entry.rank}</TableCell>
                                    <TableCell className="text-center">
                                        <ChangeIndicator value={entry.rank_change} isRank={true} />
                                    </TableCell>
                                    <TableCell className="font-medium">
                                        {entry.pseudonym}
                                        {entry.user_id === user?.id && (
                                            <span className="ml-2 text-xs text-primary">(Du)</span>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-center font-heading text-lg tabular-nums">{entry.total_points}</TableCell>
                                    <TableCell className="text-center">
                                        <ChangeIndicator value={entry.points_change} />
                                    </TableCell>
                                    <TableCell className="text-center font-mono text-sm">
                                        {entry.last_prediction || '-'}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {leaderboard.length === 0 && (
                <Card className="p-8 text-center">
                    <Trophy className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">Noch keine Spieler in der Rangliste</p>
                </Card>
            )}
        </div>
    );
}
